import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { setOptions, importLibrary } from '@googlemaps/js-api-loader';

export interface SelectedPlace {
  address: string;
  latitude: number;
  longitude: number;
  placeId?: string;
}

interface LocationAutocompleteInputProps {
  label?: string;
  placeholder?: string;
  onPlaceSelected: (place: SelectedPlace) => void;
}

interface PlaceOption {
  placeId: string;
  description: string;
}

let optionsInitialized = false;
let placesLibraryPromise: Promise<google.maps.PlacesLibrary> | null = null;

const loadPlacesLibrary = () => {
  if (!optionsInitialized) {
    setOptions({ key: import.meta.env.VITE_GOOGLE_MAPS_API_KEY, v: 'weekly' });
    optionsInitialized = true;
  }
  if (!placesLibraryPromise) {
    placesLibraryPromise = importLibrary('places') as Promise<google.maps.PlacesLibrary>;
  }
  return placesLibraryPromise;
};

const predictionText = (prediction: any): string => {
  if (!prediction) return '';
  if (typeof prediction.text === 'string') return prediction.text;
  if (typeof prediction.text?.toString === 'function') {
    const value = prediction.text.toString();
    if (value && value !== '[object Object]') return value;
  }
  return prediction.text?.text || prediction.description || '';
};

const fetchPredictions = async (
  places: google.maps.PlacesLibrary,
  input: string,
): Promise<PlaceOption[]> => {
  const AutocompleteSuggestion = (places as any).AutocompleteSuggestion;
  if (AutocompleteSuggestion?.fetchAutocompleteSuggestions) {
    const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({ input });
    return (suggestions || [])
      .map((suggestion: any) => suggestion.placePrediction)
      .filter(Boolean)
      .map((prediction: any) => ({
        placeId: prediction.placeId,
        description: predictionText(prediction),
      }))
      .filter((option: PlaceOption) => option.placeId && option.description);
  }

  return new Promise((resolve) => {
    const service = new places.AutocompleteService();
    service.getPlacePredictions({ input }, (predictions, status) => {
      if (status !== google.maps.places.PlacesServiceStatus.OK || !predictions) {
        resolve([]);
        return;
      }
      resolve(
        predictions.map((prediction) => ({
          placeId: prediction.place_id,
          description: prediction.description,
        })),
      );
    });
  });
};

const fetchPlaceDetails = async (
  places: google.maps.PlacesLibrary,
  placeId: string,
): Promise<SelectedPlace | null> => {
  if ((places as any).Place) {
    const place = new (places as any).Place({ id: placeId });
    await place.fetchFields({ fields: ['formattedAddress', 'location', 'id'] });
    if (!place.location) return null;
    return {
      address: place.formattedAddress || '',
      latitude: place.location.lat(),
      longitude: place.location.lng(),
      placeId: place.id || placeId,
    };
  }

  return new Promise((resolve) => {
    const service = new places.PlacesService(document.createElement('div'));
    service.getDetails(
      { placeId, fields: ['formatted_address', 'geometry', 'place_id'] },
      (place, status) => {
        const location = place?.geometry?.location;
        if (status !== google.maps.places.PlacesServiceStatus.OK || !place || !location) {
          resolve(null);
          return;
        }
        resolve({
          address: place.formatted_address || '',
          latitude: location.lat(),
          longitude: location.lng(),
          placeId: place.place_id,
        });
      },
    );
  });
};

export const LocationAutocompleteInput = ({
  label = 'Search for an address',
  placeholder = 'Start typing an address...',
  onPlaceSelected,
}: LocationAutocompleteInputProps) => {
  const onPlaceSelectedRef = useRef(onPlaceSelected);
  onPlaceSelectedRef.current = onPlaceSelected;

  const [libraryReady, setLibraryReady] = useState(false);
  const [loadingPredictions, setLoadingPredictions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [options, setOptions] = useState<PlaceOption[]>([]);
  const [inputValue, setInputValue] = useState('');
  const placesRef = useRef<google.maps.PlacesLibrary | null>(null);

  useEffect(() => {
    let cancelled = false;

    loadPlacesLibrary()
      .then((places) => {
        if (cancelled) return;
        placesRef.current = places;
        setLibraryReady(true);
      })
      .catch(() => {
        if (!cancelled) {
          setError('Failed to load Google Maps. Check the configured API key.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const places = placesRef.current;
    const query = inputValue.trim();
    if (!places || !libraryReady || query.length < 2) {
      setOptions([]);
      return;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      setLoadingPredictions(true);
      try {
        const predictions = await fetchPredictions(places, query);
        if (!cancelled) {
          setOptions(predictions);
        }
      } catch {
        if (!cancelled) {
          setOptions([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingPredictions(false);
        }
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [inputValue, libraryReady]);

  return (
    <Autocomplete
      freeSolo
      filterOptions={(items) => items}
      options={options}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
      isOptionEqualToValue={(option, value) => option.placeId === value.placeId}
      inputValue={inputValue}
      onInputChange={(_event, value) => setInputValue(value)}
      onChange={async (_event, value) => {
        if (!value || typeof value === 'string' || !placesRef.current) return;
        const details = await fetchPlaceDetails(placesRef.current, value.placeId);
        if (details) {
          onPlaceSelectedRef.current(details);
        }
      }}
      loading={loadingPredictions || !libraryReady}
      slotProps={{
        popper: { sx: { zIndex: 1400 } },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          fullWidth
          error={!!error}
          helperText={error}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loadingPredictions || !libraryReady ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};
